/* Shopify cart or opt-in server draft preparation; no payment or code allocation. */
(function () {
  function validEndpoint(endpoint) {
    try {
      if (typeof endpoint !== 'string' || endpoint !== endpoint.trim() || endpoint.length > 2048) return false;
      const url = new URL(endpoint);
      return url.protocol === 'https:' && !url.username && !url.password && !url.hash && !url.search && url.pathname !== '/';
    } catch { return false; }
  }
  function validInvoiceURL(value) {
    try {
      if (typeof value !== 'string' || /[\u0000-\u0020\u007f]/.test(value)) return false;
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password && !url.hash && !url.port &&
        ['getyangoplay.com', 'checkout.getyangoplay.com', 'pgcrwf-r1.myshopify.com'].includes(url.hostname) &&
        /^\/(?:\d+\/)?(?:invoices?\/[A-Za-z0-9_-]{16,}|checkouts\/(?:cn\/)?[A-Za-z0-9_-]{16,})\/?$/.test(url.pathname);
    } catch { return false; }
  }
  function draftQuote(value, product, quantity) {
    if (!value || !['unitPrice', 'subtotal', 'discount', 'total'].every(key => Number.isSafeInteger(value[key]) && value[key] >= 0) ||
      !Number.isFinite(value.percent) || value.percent < 0 || value.percent > 100 || !/^[A-Z]{3}$/.test(value.currency) || value.currency !== product.currency ||
      value.quantity !== quantity || String(value.variantId) !== String(product.id) ||
      value.subtotal !== value.unitPrice * quantity || value.discount > value.subtotal || value.total !== value.subtotal - value.discount) return null;
    return { unitPrice: value.unitPrice, subtotal: value.subtotal, discount: value.discount, total: value.total,
      percent: value.percent, currency: value.currency, quantity, variantId: String(product.id) };
  }
  function draftBody({ product, quantity, market, locale, expected, attribution = {}, requestId, maxQuantity }) {
    const fail = reason => { throw Object.assign(new Error(reason), { reason }); };
    if (!product?.available || !Number.isSafeInteger(maxQuantity) || maxQuantity < 1 || !Number.isInteger(quantity) || quantity < 1 || quantity > stockLimit(product, maxQuantity)) fail('stock');
    if (!/^[1-9]\d*$/.test(String(product.id)) || !Number.isSafeInteger(Number(product.id)) || !['AE','KW','QA'].includes(market) || !['en','ar'].includes(locale) ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId)) fail('request');
    const quote = draftQuote({ ...expected, quantity, variantId: product.id }, product, quantity);
    if (!quote || quote.unitPrice !== product.price) fail('price');
    const clickId = key => typeof attribution[key] === 'string' && attribution[key].length <= 512 ? attribution[key] : '';
    return { requestId, variantId: Number(product.id), quantity, market, locale,
      expected: { unitPrice: quote.unitPrice, subtotal: quote.subtotal, discount: quote.discount, total: quote.total, percent: quote.percent, currency: quote.currency },
      attribution: { fbclid: clickId('fbclid'), ttclid: clickId('ttclid') } };
  }
  async function prepareDraft(options) {
    const fail = (reason, details = {}) => { throw Object.assign(new Error(reason), { reason, ...details }); };
    if (!validEndpoint(options.endpoint)) fail('request');
    const body = draftBody(options);
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await (options.fetch || globalThis.fetch)(options.endpoint, { method: 'POST', credentials: 'omit', cache: 'no-store', redirect: 'error',
        signal: controller.signal, headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (result?.error) {
        if (!['priceChanged','stock','price','request','pending'].includes(result.error)) fail('request');
        if (result.error === 'priceChanged') {
          const quote = draftQuote(result.quote, options.product, options.quantity);
          if (!quote) fail('price');
          fail('priceChanged', { quote });
        }
        fail(result.error, result.error === 'stock' && Number.isSafeInteger(result.stock) && result.stock >= 0 ? { stock: result.stock } : {});
      }
      if (!response.ok) fail('request');
      const quote = draftQuote(result?.quote, options.product, options.quantity);
      if (!quote || Object.keys(body.expected).some(key => quote[key] !== body.expected[key])) fail('price');
      if (!validInvoiceURL(result.checkoutUrl) || typeof result.expiresAt !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/.test(result.expiresAt) ||
        !Number.isFinite(Date.parse(result.expiresAt)) || Date.parse(result.expiresAt) <= Date.now()) fail('request');
      // Keep Shopify's signed query byte-for-byte; the server owns locale.
      return { checkoutUrl: result.checkoutUrl, quote, expiresAt: result.expiresAt };
    } catch (error) {
      if (['priceChanged','stock','price','request','pending'].includes(error.reason)) throw error;
      fail('request');
    } finally { clearTimeout(timeout); }
  }
  function createDraftClient({ endpoint, randomUUID = () => globalThis.crypto.randomUUID(), fetch } = {}) {
    if (!validEndpoint(endpoint)) throw Object.assign(new Error('request'), { reason: 'request' });
    let fingerprint, requestId;
    return {
      invalidate() { fingerprint = undefined; requestId = undefined; },
      async prepare(options) {
        const body = draftBody({ ...options, requestId: '00000000-0000-4000-8000-000000000000' });
        const key = JSON.stringify({ ...body, requestId: undefined });
        if (key !== fingerprint) { requestId = randomUUID(); fingerprint = key; }
        return prepareDraft({ ...options, endpoint, requestId, fetch });
      }
    };
  }
  function matchesOrder(cart, product) {
    return cart.items?.length === 1 && String(cart.items[0].variant_id) === String(product.id)
      && cart.items[0].properties?._yp_landing === 'b2b';
  }
  function stockLimit(product, max) {
    return product?.tracked === true && product.inventoryPolicy === 'deny' && Number.isInteger(product.stock)
      ? Math.max(0, Math.min(max, product.stock)) : 0;
  }
  function verifyCart(cart, product, quantity, expected, attributes) {
    if (!matchesOrder(cart, product) || cart.items[0].quantity !== quantity || cart.item_count !== quantity) return 'cart';
    if (cart.currency !== product.currency || cart.original_total_price !== expected.subtotal
      || cart.total_price !== expected.total || cart.total_discount !== expected.discount
      || cart.items[0].original_line_price !== expected.subtotal || cart.items[0].final_line_price !== expected.total) return 'price';
    const allocations = cart.items[0].line_level_discount_allocations || [];
    const titles = { 10: 'Volume 10% · 1+ vouchers', 20: 'Volume 20% · 100+ vouchers', 35: 'Volume 35% · 500+ vouchers', 40: 'Volume 40% · 2000+ vouchers', 50: 'Volume 50% · 5000+ vouchers' };
    if (cart.cart_level_discount_applications?.length || (expected.discount === 0 ? allocations.length !== 0 :
      allocations.length !== 1 || allocations[0].amount !== expected.discount ||
      allocations[0].discount_application?.title !== titles[expected.percent] ||
      allocations[0].discount_application?.value_type !== 'percentage' ||
      Number(allocations[0].discount_application?.value) !== expected.percent)) return 'price';
    if (Object.entries(attributes).some(([key, value]) => (cart.attributes?.[key] || '') !== value)) return 'attributes';
    return null;
  }
  async function prepare({ product, quantity, expected, attributes, maxQuantity, replace = false, request }) {
    const fail = reason => { throw Object.assign(new Error(reason), { reason }); };
    if (!product?.available || !Number.isInteger(quantity) || quantity < 1 || quantity > stockLimit(product, maxQuantity)) fail('stock');
    let cart = await request('cart.js');
    if (cart.items?.length && !matchesOrder(cart, product)) {
      if (!replace) fail('conflict');
      await request('cart/clear.js', {});
      cart = { items: [] };
    }
    if (matchesOrder(cart, product)) {
      await request('cart/change.js', { id: cart.items[0].key, quantity });
    } else {
      await request('cart/add.js', { items: [{ id: product.id, quantity, properties: { _yp_landing: 'b2b' } }] });
    }
    await request('cart/update.js', { attributes, discount: '' });
    cart = await request('cart.js');
    const reason = verifyCart(cart, product, quantity, expected, attributes);
    if (reason) throw Object.assign(new Error(reason), { reason, cart });
    return cart;
  }
  const api = { matchesOrder, stockLimit, verifyCart, prepare, validEndpoint, validInvoiceURL, prepareDraft, createDraftClient };
  if (typeof module !== 'undefined') module.exports = api;
  if (typeof window !== 'undefined') window.YPB2BCheckout = Object.freeze(api);
})();
